import { useCallback, useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useAccessToken } from '@/services/auth';
import {
  AddEmbryosToShipmentInputV2,
  EmbryoDto,
  embryoServiceApi,
  ShipmentConflict,
} from '@/services/embryo-service';
import { shipmentEmbryosOptions } from '@/services/shipping';

type UseAddEmbryosToShipmentOptions = {
  setLoading: (loading: boolean) => void;
  onSuccess: () => void;
};

export function useAddEmbryosToShipment({
  setLoading,
  onSuccess,
}: UseAddEmbryosToShipmentOptions) {
  const token = useAccessToken();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const isOverrideEnabled = useFeatureFlag('Embryo_Shipment_Override');
  const isEmbryoRemoveFromShipmentEnabled = useFeatureFlag(
    'Embryo_Remove_From_Shipment',
  );

  const [conflicts, setConflicts] = useState<ShipmentConflict[]>([]);
  const [pendingInput, setPendingInput] =
    useState<AddEmbryosToShipmentInputV2 | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setLoadingRef = useRef(setLoading);
  const onSuccessRef = useRef(onSuccess);

  useEffect(() => {
    setLoadingRef.current = setLoading;
    onSuccessRef.current = onSuccess;
  });

  const invalidateShipmentEmbryos = useCallback(
    (shipmentIds: string[]) => {
      for (const shipmentId of new Set(shipmentIds)) {
        queryClient.invalidateQueries({
          queryKey: shipmentEmbryosOptions(token, shipmentId).queryKey,
        });
      }
    },
    [queryClient, token],
  );

  const submit = useCallback(
    async (input: AddEmbryosToShipmentInputV2) => {
      setLoadingRef.current(true);
      try {
        const result = await embryoServiceApi.addEmbryosToShipmentV2(input);

        if (isOverrideEnabled && result.requiresOverride) {
          const overrideConflicts = result.conflicts ?? [];

          if (
            input.overrideExistingShipment ||
            overrideConflicts.length === 0
          ) {
            setConflicts([]);
            setPendingInput(null);
            dispatch(
              addToast({
                severity: 'error',
                summary: t('common.error'),
                detail: result.message ?? t('shipping.failedToAddEmbryos'),
                life: ToastLife.LONG,
              }),
            );
            return;
          }

          setConflicts(overrideConflicts);
          setPendingInput(input);
          return;
        }

        const success = result.success ?? false;
        const message = result.message ?? 'Operation completed';

        const isWarning = message.toLowerCase().includes('warning');
        const severity = isWarning ? 'warn' : success ? 'success' : 'error';
        const summary = isWarning
          ? t('common.warning')
          : success
            ? t('common.success')
            : t('common.error');
        const life = isWarning || !success ? ToastLife.LONG : ToastLife.DEFAULT;

        dispatch(
          addToast({
            severity,
            summary,
            detail: message,
            life,
          }),
        );

        if (success) {
          setConflicts([]);
          setPendingInput(null);
          onSuccessRef.current();
          if (isEmbryoRemoveFromShipmentEnabled) {
            invalidateShipmentEmbryos([
              input.shipmentId,
              ...(input.overrideExistingShipment
                ? conflicts.map((conflict) => conflict.shipmentId)
                : []),
            ]);
          }
        }
      } catch (error) {
        dispatch(
          addToast({
            severity: 'error',
            summary: t('common.error'),
            detail:
              error instanceof Error
                ? error.message
                : t('shipping.failedToAddEmbryos'),
            life: ToastLife.DEFAULT,
          }),
        );
      } finally {
        setLoadingRef.current(false);
      }
    },
    [
      conflicts,
      dispatch,
      invalidateShipmentEmbryos,
      isEmbryoRemoveFromShipmentEnabled,
      isOverrideEnabled,
      t,
    ],
  );

  const addEmbryosToShipmentV2 = useCallback(
    async (shipmentId: string | undefined, selectedEmbryos: EmbryoDto[]) => {
      if (selectedEmbryos.length === 0) {
        return;
      }

      const embryoList = selectedEmbryos.flatMap((embryo) =>
        embryo.id ? [{ embryoId: embryo.id }] : [],
      );

      if (!shipmentId || embryoList.length !== selectedEmbryos.length) {
        return;
      }

      await submit({ shipmentId, embryoList });
    },
    [submit],
  );

  const confirmOverride = useCallback(async () => {
    if (!pendingInput) {
      return;
    }
    setIsSubmitting(true);
    try {
      await submit({ ...pendingInput, overrideExistingShipment: true });
    } finally {
      setIsSubmitting(false);
    }
  }, [pendingInput, submit]);

  const cancelOverride = useCallback(() => {
    setConflicts([]);
    setPendingInput(null);
    setIsSubmitting(false);
  }, []);

  return {
    addEmbryosToShipmentV2,
    conflicts,
    isOverrideEnabled,
    isOverrideDialogVisible: pendingInput !== null,
    isSubmitting,
    confirmOverride,
    cancelOverride,
  };
}
